import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { postApi } from '../utils/api';
import { contact } from '../utils/endpoint';
import ErrorBanner from '../Pages/ErrorBanner';

const initialValues = {
  name: '',
  email: '',
  phone: '',
  contact: '',
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phonePattern = /^[0-9()+\-\s]{7,20}$/;

const ContactForm = ({
  successMessage,
  prefillName,
  prefillEmail,
  prefillPhone,
}) => {
  const [formValues, setFormValues] = useState({
    name: prefillName || '',
    email: prefillEmail || '',
    phone: prefillPhone || '',
    contact: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [banner, setBanner] = useState(null);

  const validateValues = (values) => {
    const errors = {};

    if (!values.name.trim()) {
      errors.name = 'Name is required.';
    }

    if (!values.email.trim()) {
      errors.email = 'Email is required.';
    } else if (!emailPattern.test(values.email.trim())) {
      errors.email = 'Enter a valid email address.';
    }

    if (!values.phone.trim()) {
      errors.phone = 'Phone number is required.';
    } else if (!phonePattern.test(values.phone.trim())) {
      errors.phone = 'Enter a valid phone number.';
    }

    if (!values.contact.trim()) {
      errors.contact = 'Contact message is required.';
    }

    return errors;
  };

  const handleCloseBanner = () => {
    setBanner(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormValues((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    setFormErrors((prevState) => ({
      ...prevState,
      [name]: '',
    }));

    if (banner) {
      setBanner(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateValues(formValues);
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        name: formValues.name.trim(),
        email: formValues.email.trim(),
        phone: formValues.phone.trim(),
        contact: formValues.contact.trim(),
      };

      const response = await postApi(contact, payload);
      setBanner({
        isError: false,
        message:
          response?.data?.message ||
          successMessage ||
          'Thanks for contacting us. We will get back to you soon.',
      });
      setFormValues(initialValues);
      setFormErrors({});
    } catch (err) {
      const apiErrors = err?.response?.data?.errors || {};
      const nextErrors = {
        name: apiErrors?.name?.[0] || '',
        email: apiErrors?.email?.[0] || '',
        phone: apiErrors?.phone?.[0] || '',
        contact: apiErrors?.contact?.[0] || '',
      };

      if (Object.values(nextErrors).some(Boolean)) {
        setFormErrors(nextErrors);
      }

      setBanner({
        isError: true,
        message:
          err?.response?.data?.message ||
          'Unable to submit your message. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {banner && <ErrorBanner error={banner} handleClose={handleCloseBanner} />}
      <form onSubmit={handleSubmit} noValidate>
        <div className="row">
          <div className="col-md-6">
            <div className="form-floating mb-3">
              <input
                id="contact-name"
                type="text"
                name="name"
                className="form-control form-input"
                placeholder="Name"
                value={formValues.name}
                onChange={handleChange}
                aria-invalid={Boolean(formErrors.name)}
              />
              <label htmlFor="contact-name">Name*</label>
              {formErrors.name && (
                <div className="text-danger small mt-1">{formErrors.name}</div>
              )}
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-floating mb-3">
              <input
                id="contact-email"
                type="email"
                name="email"
                className="form-control form-input"
                placeholder="Email"
                value={formValues.email}
                onChange={handleChange}
                aria-invalid={Boolean(formErrors.email)}
              />
              <label htmlFor="contact-email">Email*</label>
              {formErrors.email && (
                <div className="text-danger small mt-1">{formErrors.email}</div>
              )}
            </div>
          </div>
          <div className="col-12">
            <div className="form-floating mb-3">
              <input
                id="contact-phone"
                type="tel"
                name="phone"
                className="form-control form-input"
                placeholder="Phone"
                value={formValues.phone}
                onChange={handleChange}
                aria-invalid={Boolean(formErrors.phone)}
              />
              <label htmlFor="contact-phone">Phone*</label>
              {formErrors.phone && (
                <div className="text-danger small mt-1">{formErrors.phone}</div>
              )}
            </div>
          </div>
          <div className="col-12">
            <div className="form-floating mb-3">
              <textarea
                id="contact-message"
                name="contact"
                className="form-control form-input"
                placeholder="Contact Message"
                rows={5}
                style={{ overflowY: 'auto' }}
                value={formValues.contact}
                onChange={handleChange}
                aria-invalid={Boolean(formErrors.contact)}
              ></textarea>
              <label htmlFor="contact-message">Contact Message*</label>
              {formErrors.contact && (
                <div className="text-danger small mt-1">
                  {formErrors.contact}
                </div>
              )}
            </div>
          </div>
          <div className="col-12">
            <button
              type="submit"
              className={`theme-button login-button mt-2 ${
                isSubmitting ? 'disabled-btn' : ''
              }`}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </div>
      </form>
    </>
  );
};

ContactForm.propTypes = {
  successMessage: PropTypes.string,
  prefillName: PropTypes.string,
  prefillEmail: PropTypes.string,
  prefillPhone: PropTypes.string,
};

export default ContactForm;
